import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  IconButton,
  HStack,
  Card,
  CardHeader,
  CardBody,
  Spacer,
  useColorModeValue,
  Switch,
} from "@chakra-ui/react";
import { FaTrash } from "react-icons/fa";

const SurveyForm = ({ onSubmit, onCancel, initialData = {} }) => {
  const [surveyName, setSurveyName] = useState(initialData.name || "");
  const [language, setLanguage] = useState(initialData.language || "");
  const [endMessage, setEndMessage] = useState(initialData.end_message || "");
  const [groups, setGroups] = useState(
    initialData.survey_groups?.map((group) => ({
      ...group,
      localId: group.id || Date.now() + Math.random(), // Unique ID
    })) || [{ name: "", leadership: false, localId: Date.now() + Math.random() }]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      name: surveyName,
      language,
      end_message: endMessage,
      survey_groups_attributes: groups.filter(group => group.name.trim() !== "").map(({ name, leadership, id, _destroy }) => ({
        name,
        leadership,
        id,
        _destroy,
      })),
    });
  };

  const handleGroupChange = (localId, field, value) => {
    setGroups((prevGroups) =>
      prevGroups.map((group) =>
        group.localId === localId ? { ...group, [field]: value } : group
      )
    );
  };

  const handleAddGroup = () => {
    setGroups((prevGroups) => [
      ...prevGroups,
      { name: "", leadership: false, localId: Date.now() + Math.random() },
    ]);
  };

  const handleRemoveGroup = (localId) => {
    setGroups((prevGroups) =>
      prevGroups
        .map((group) =>
          group.localId === localId
            ? { ...group, _destroy: group.id ? true : undefined }
            : group
        )
        .filter((group) => group._destroy !== undefined || !group._destroy)
    );
  };

  const cardBg = useColorModeValue("white", "gray.800");
  const sectionBg = useColorModeValue("gray.50", "gray.700");

  return (
    <Box mx="auto" p={6} maxW="container.md">
      <Card bg={cardBg} shadow="md" borderRadius="lg">
        <CardHeader pb={0} textAlign="center">
          <FormLabel fontSize="xl" fontWeight="bold">
            {initialData.name ? "Edit Survey" : "Create Survey"}
          </FormLabel>
        </CardHeader>

        <CardBody>
          <form onSubmit={handleSubmit}>
            <VStack spacing={5} align="stretch">
              {/* Survey Name */}
              <Box bg={sectionBg} p={4} borderRadius="md">
                <FormControl isRequired>
                  <FormLabel>Survey Name</FormLabel>
                  <Input
                    type="text"
                    value={surveyName}
                    onChange={(e) => setSurveyName(e.target.value)}
                    placeholder="Enter survey name"
                  />
                </FormControl>
              </Box>

              {/* Language Selection */}
              <Box bg={sectionBg} p={4} borderRadius="md">
                <FormControl isRequired>
                  <FormLabel>Language</FormLabel>
                  <Select
                    placeholder="Select language"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    <option value="english">English</option>
                    <option value="swedish">Swedish</option>
                  </Select>
                </FormControl>
              </Box>

              {/* End Message */}
              <Box bg={sectionBg} p={4} borderRadius="md">
                <FormControl>
                  <FormLabel>End Message</FormLabel>
                  <Textarea
                    value={endMessage}
                    onChange={(e) => setEndMessage(e.target.value)}
                    placeholder="Enter a message to display after survey completion"
                    size="md"
                  />
                </FormControl>
              </Box>

              {/* Survey Profession (Groups) */}
              <Box bg={sectionBg} p={4} borderRadius="md">
                <FormControl>
                  <FormLabel>Survey Profession</FormLabel>

                  {/* Header Row */}
                  <HStack fontWeight="medium" px={1}>
                    <Box flex="1">Name</Box>
                    <Box w="120px" textAlign="center">
                      Leadership
                    </Box>
                    <Box w="40px" />
                  </HStack>

                  {/* Group Fields */}
                  <VStack spacing={2} align="stretch">
                    {groups
                      .filter((group) => !group._destroy)
                      .map((group) => {
                        const switchId = `leadership-switch-${group.localId}`;
                        return (
                          <HStack key={group.localId} spacing={3}>
                            <Input
                              flex="1"
                              placeholder="Enter Profession name"
                              value={group.name}
                              onChange={(e) =>
                                handleGroupChange(group.localId, "name", e.target.value)
                              }
                            />
                            <FormControl display="flex" alignItems="center" w="120px" justifyContent="center">
                              <Switch
                                id={switchId}
                                isChecked={group.leadership || false}
                                onChange={(e) =>
                                  handleGroupChange(group.localId, "leadership", e.target.checked)
                                }
                                colorScheme="green"
                              />
                            </FormControl>
                            <IconButton
                              icon={<FaTrash />}
                              colorScheme="red"
                              size="sm"
                              variant="outline"
                              aria-label="Remove profession"
                              title="Remove profession"
                              onClick={() => handleRemoveGroup(group.localId)}
                            />
                          </HStack>
                        );
                      })}
                  </VStack>

                  <Button
                    mt={2}
                    onClick={handleAddGroup}
                    variant="outline"
                    colorScheme="blue"
                    size="sm"
                  >
                    Add Profession
                  </Button>
                </FormControl>
              </Box>

              {/* Submit & Cancel Buttons */}
              <Flex>
                <Button variant="outline" onClick={onCancel} size="sm">
                  Cancel
                </Button>
                <Spacer />
                <Button type="submit" colorScheme="green" size="sm" variant="outline">
                  {initialData.name ? "Update Survey" : "Create Survey"}
                </Button>
              </Flex>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
};

export default SurveyForm;